import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';

import { DimensionValueScoreSelectable } from 'src/app/shared/complexity-mgnt/dimension-value-score-selectable';
import { ComplexityMngtService } from 'src/app/shared/complexity-mgnt/complexity-mngt.service';

@Component({
  selector: 'top-vals-modal',
  templateUrl: 'top-vals-modal.component.html',
  styleUrls: ['../general.scss']
})
export class TopValsModalComponent {
    
    TopValsSort: typeof TopValsSort = TopValsSort;   //hack for enum to be available in the template
    public loading: boolean = true;
    public projectId: string;
    public columnName: string;
    public values: DimensionValueScoreSelectable[] = [];

    public sortAsc: boolean = true;
    public sortBy: TopValsSort = TopValsSort.Relevance;
    public showHint = true;
    public topNOptions: number[] = [];

    private masterCheckboxStep: number = 0;
    private savedSelection: boolean[] = [];

    public get selectedCount(): number {
        return this.values.filter(v => v.selected).length;
    }

    public get allSelected(): boolean {
        return this.values.length > 0 && this.values.every(v => v.selected);
    }

    public get someSelected(): boolean {
        return this.values.some(v => v.selected) && !this.allSelected;
    }

    constructor(@Inject(MAT_DIALOG_DATA) data: any, private dialogRef: MatDialogRef<TopValsModalComponent>, private complexityMngtService: ComplexityMngtService) {
        this.projectId = data.projectId;
        this.columnName = data.columnName;
        this.topNOptions = this.computeTopNOptions(data.compactTopN);
        console.log('injected data:');
        console.log(data);
    }
    
    ngOnInit(): void {
        this.loading = true;
        this.complexityMngtService.getTopVals(this.projectId, this.columnName)
        .then(resp => {
            if (!resp.selectedValues || !resp.selectedValues.length) {
                this.dialogRef.close();
            }
            this.values = resp.selectedValues;
            this.loading = false;
        }).catch(err => {
            console.error(err);
            this.dialogRef.close();
        });
    }

    public async save () {
        if (this.loading) {
            return;
        }
        this.loading = true;
        try {
            await this.complexityMngtService.saveTopVals(this.projectId, this.columnName, this.values.filter(v => v.selected).map(v => v.value));
            this.dialogRef.close();
        } finally {
            this.loading = false;
        };
    }

    private computeTopNOptions(compactTopN: number): number[] {
        if (!compactTopN || compactTopN <= 0) {
            return [10, 20, 30];
        }
        const b = compactTopN;
        const c = Math.pow(10, Math.floor(Math.log10(Math.abs(b) / 5) + 1e-9)); // small epsilon guards floating point error at exact powers of 10
        const d = Math.abs(b / c) >= 10 ? c : c / 2;
        const option1 = Math.round(Math.max(Math.min(b, 5), d * 2));
        const option2 = Math.round(Math.max(Math.min(b, 10), d * 3.5));
        const option3 = Math.round(Math.max(Math.min(b, 20), d * 5));
        const options = [option1, option2];
        if (option3 !== option2) {
            options.push(option3);
        }
        return options;
    }

    public selectTopN(n: number) {
        for (let i = 0; i < this.values.length; ++i) {
            this.values[i].selected = i < n;
        }
        this.masterCheckboxStep = 0;
    }

    public toggleMasterCheckbox() {
        if (this.loading || !this.values.length) {
            return;
        }
        switch (this.masterCheckboxStep) {
            case 0: // select all
                this.savedSelection = this.values.map(v => v.selected);
                this.values.forEach(v => v.selected = true);
                this.masterCheckboxStep = 1;
                break;
            case 1: // clear selection
                this.values.forEach(v => v.selected = false);
                this.masterCheckboxStep = 2;
                break;
            default: // re-select previous
                this.values.forEach((v, i) => v.selected = !!this.savedSelection[i]);
                this.masterCheckboxStep = 0;
                break;
        }
    }

    public onValueToggle() {
        this.masterCheckboxStep = 0;
    }

    public sort(sortBy: TopValsSort) {
        if (this.sortBy == sortBy) {
            this.sortAsc = !this.sortAsc;
        } else {
            this.sortBy = sortBy;
            switch (sortBy) {
                case TopValsSort.Relevance:
                    case TopValsSort.Value:
                    this.sortAsc = true;
                    break;
                case TopValsSort.Complexity:
                case TopValsSort.Outcomes:
                case TopValsSort.Variance:
                    this.sortAsc = false;
                    break;

            }
        }
        let sorter : (a: DimensionValueScoreSelectable, b: DimensionValueScoreSelectable) => number;
        switch (sortBy) {
            case TopValsSort.Relevance:
                if (this.sortAsc) {
                    sorter = (a, b) => a.relevance - b.relevance;
                } else {
                    sorter = (a, b) => b.relevance - a.relevance;
                }
                break;
            case TopValsSort.Value:
                let sorterLocal = (a: DimensionValueScoreSelectable, b: DimensionValueScoreSelectable) => {
                    var nameA = a.value.toLowerCase(), nameB = b.value.toLowerCase();
                    if (nameA < nameB) {
                        return -1;
                    }
                    if (nameA > nameB) {
                        return 1;
                    }
                    return 0;
                }
                if (this.sortAsc) {
                    sorter = (a, b) => sorterLocal(a, b);
                } else {
                    sorter = (a, b) => -sorterLocal(a, b);
                }
                break;
            case TopValsSort.Complexity:
                if (this.sortAsc) {
                    sorter = (a, b) => a.complexityScore - b.complexityScore;
                } else {
                    sorter = (a, b) => b.complexityScore - a.complexityScore;
                }
                break;
            case TopValsSort.Outcomes:
                if (this.sortAsc) {
                    sorter = (a, b) => a.outcomesScore - b.outcomesScore;
                } else {
                    sorter = (a, b) => b.outcomesScore - a.outcomesScore;
                }
                break;
            case TopValsSort.Variance:
                if (this.sortAsc) {
                    sorter = (a, b) => a.varianceScore - b.varianceScore;
                } else {
                    sorter = (a, b) => b.varianceScore - a.varianceScore;
                }
                break;
        }
        this.values = this.values.sort(sorter);
    }

}

export enum TopValsSort {
    Relevance,
    Value,
    Complexity,
    Outcomes,
    Variance,
}